/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { NgZone } from '@angular/core';
import { Control } from 'ol/control';

import { GoogleControlConfig } from '../../../types/Google';

export class GoogleLayersControl extends Control {
    private readonly _layerConfigs: Map<string, GoogleControlConfig>;
    private _type: string;
    private _imageryEnabled: boolean;
    private zone: NgZone;

    constructor(optOptions) {
        const defaultOptions = { initialLayer: 'roadmap' };
        const options = { ...defaultOptions, ...optOptions };
        const element: HTMLDivElement = document.createElement('div');
        super({
            ...options,
            ...{ element: element, target: options.target },
        });
        this.initButtons(options, element);

        this._type = options.initialLayer;
        /** @var {Map} */
        this._layerConfigs = options.layers;
    }

    public setType(type) {
        this._type = type;
        this.updateButtons();
    }

    public enableImagery() {
        this._imageryEnabled = true;
        this.updateButtons();
    }

    public disableImagery() {
        this._imageryEnabled = false;
        this.updateButtons();
    }

    public setZone(zone: NgZone): GoogleLayersControl {
        this.zone = zone;
        return this;
    }

    private initButtons(options, element) {
        const defaultLayer = options.initialLayer || 'roadmap';

        element.classList.add('choose-base');
        element.classList.add('ol-unselectable');
        element.classList.add('ol-control');

        for (const [layerType, layerConfig] of options.layers) {
            const button: HTMLButtonElement = document.createElement('button');
            button.innerText = layerConfig.label;
            button.id = `button_${layerType}`;

            if (layerType === defaultLayer) {
                button.classList.add('active');
            }
            const setLayerListener = () => {
                this.setType(layerType);
                layerConfig.callback();
            };
            button.addEventListener('click', setLayerListener);
            button.addEventListener('touchstart', setLayerListener);
            element.appendChild(button);
        }
    }

    private updateButtons() {
        for (const [type, config] of this._layerConfigs) {
            const target = document.getElementById(`button_${type}`);
            if (this._type === type) {
                // type is selected, so disable the button
                target.classList.add('active');
            } else {
                // enable, but only if it's not an imagery layer or imagery is enabled
                target.classList.remove('active');
                if (config.isImagery && !this._imageryEnabled) {
                    target.setAttribute('disabled', 'true');
                } else {
                    if (target.hasAttribute('disabled')) {
                        if (this.zone) {
                            this.zone.runOutsideAngular(() => {
                                window.requestAnimationFrame(() => {
                                    target.getAttribute('disabled');
                                    target.removeAttribute('disabled');
                                });
                            });
                        } else {
                            // not ideal but shouldn't break
                            window.requestAnimationFrame(() => {
                                target.getAttribute('disabled');
                                target.removeAttribute('disabled');
                            });
                        }
                    }
                }
            }
        }
    }
}
